// Oct 20 (persistent pilot): the Owner / an HR administrator sets up their OWN basic HR record so they can use My HR like any employee.
//
// The three fields below are the ONLY basic self-setup fields: start date, job title, department. Everything else - manager,
// classification, schedule, access grants, compensation, and approving their own later changes - stays with the existing HR rules and
// is NOT set here. The server accepts exactly these three fields and refuses anything else.
//
// Oct 20 correction (Owner UAT): the card can be opened from your own row in the Employees list (`openSignal`), and a successful save is CONFIRMED on
// screen. Before, the card simply vanished once the access context reloaded, so a real success looked the same as nothing happening.
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useHrAccess } from './HrAccessContext';
import { listHrDepartments, selfSetupHrRecord } from '../../api/client';

export function SelfSetupCard({ openSignal = 0, onSaved }: { openSignal?: number; onSaved?: () => void }) {
  const { state, refresh } = useHrAccess();
  const idBase = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [hireDate, setHireDate] = useState('');
  const [title, setTitle] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [departments, setDepartments] = useState<Array<{ departmentId: string; name: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  const available = state.kind === 'ready' && state.me.selfSetupAvailable === true && !state.me.hasHrRecord;

  useEffect(() => {
    if (!open) return;
    listHrDepartments()
      .then((d: any) => setDepartments(d.departments ?? []))
      .catch(() => setDepartments([]));
  }, [open]);

  // Asked to open from outside (your own row in the list): open the form and bring it into view.
  useEffect(() => {
    if (openSignal > 0 && available) {
      setOpen(true);
      setSavedNote(null);
      rootRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    }
  }, [openSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!available && !savedNote) return null;

  async function submit() {
    if (!hireDate || saving) return;
    setSaving(true);
    setError(null);
    try {
      await selfSetupHrRecord({
        hireDate,
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(departmentId ? { departmentId } : {}),
      });
      // The server confirmed the save. Say so BEFORE the context reload swaps this card out.
      setSavedNote('Your HR record is set up. My HR is now available to you.');
      setOpen(false);
      onSaved?.();
      refresh(); // surfaces.myHr becomes true: My HR appears for this person. A soft refresh: this screen stays put, so the confirmation above stays visible.
    } catch (e: any) {
      // Cycle 13: the server answers 409 { error: 'CONFLICT', message: 'You already have an HR record.', existing: true } when the record is already there (including the loser of a
      // simultaneous save). The message is shown as it is; refreshing the access context lets this card give way to My HR instead of offering a form that can no longer be used.
      if (e?.status === 409 && e?.body?.existing === true) {
        setSavedNote(`${e.message || 'You already have an HR record.'} My HR is available to you.`);
        setOpen(false);
        refresh();
        return;
      }
      setError(e?.message || 'Could not set up your HR record.');
    } finally {
      setSaving(false);
    }
  }

  if (savedNote) {
    return (
      <div ref={rootRef} data-testid="self-setup-saved" role="status" className="bg-[#F0FAF4] rounded-lg border border-green-200 p-4 mb-4 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-800">
        <span>{savedNote}</span>
        <span className="flex items-center gap-3">
          <Link to="/hr" className="text-[#176A9B] font-medium hover:underline">Open My HR →</Link>
          <button type="button" onClick={() => setSavedNote(null)} className="text-xs text-gray-500 underline">Dismiss</button>
        </span>
      </div>
    );
  }

  return (
    <div ref={rootRef} id="self-setup-card" data-testid="self-setup-card" className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-900">Set up your own HR record</h2>
          <p className="text-xs text-gray-600 mt-1 max-w-xl">
            You do not have an HR record yet, so My HR is not available to you. You can add the basics yourself: start date, job title and department.
            Your manager, classification, schedule, access and pay are not set here and follow the normal HR rules.
          </p>
        </div>
        {!open && (
          <button type="button" onClick={() => setOpen(true)} className="bg-[#4AABDE] hover:bg-[#3a94c8] text-white text-sm font-medium px-4 py-2 rounded-md flex-shrink-0">
            Set up my record
          </button>
        )}
      </div>
      {open && (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label htmlFor={`${idBase}-date`} className="text-xs text-gray-500">Start date (required)</label>
            <input id={`${idBase}-date`} type="date" value={hireDate} onChange={(e) => setHireDate(e.target.value)} className="border border-gray-300 rounded px-3 py-1.5 text-sm" />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor={`${idBase}-title`} className="text-xs text-gray-500">Job title</label>
            <input id={`${idBase}-title`} type="text" maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} className="border border-gray-300 rounded px-3 py-1.5 text-sm" />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor={`${idBase}-dept`} className="text-xs text-gray-500">Department</label>
            <select id={`${idBase}-dept`} value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white min-w-[10rem]">
              <option value="">No department</option>
              {departments.map((d) => <option key={d.departmentId} value={d.departmentId}>{d.name}</option>)}
            </select>
          </div>
          <button type="button" onClick={submit} disabled={saving || !hireDate} className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? 'Saving…' : 'Save my record'}
          </button>
          <button type="button" onClick={() => { setOpen(false); setError(null); }} className="text-sm text-gray-500 px-3 py-1.5">Cancel</button>
        </div>
      )}
      {error && <div role="alert" data-testid="self-setup-error" className="text-sm text-red-700 mt-2">{error}</div>}
    </div>
  );
}
